import { useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientApi } from '../api';
import { z } from 'zod';
import {
    NewWhatsMessageEnvelopeSchema, WhatsAppStatusSocketSchema,
    WhatsAppMessageUpdateSocketSchema, WhatsAppPresenceSocketSchema,
} from '../schemas';
import { toastSink } from '../../../shared/ui/Toast/toastSink';
import { useSocket } from '../../../shared/socket/useSocket';
import { useToast } from '../../../shared/ui/Toast/ToastProvider';
import { getErrorMessage } from '../../../shared/errors/getErrorMessage';

// Superficie de datos de WhatsAppPanel.jsx -- primer hook de datos de este repo en poseer
// también el wiring de sockets (02.3-client.md §1/§4: `policies/code.md` tiene 2 cláusulas en
// tensión sobre esto, "el hook de datos" vs. "el container" -- decisión deliberada para este
// archivo, no un precedente que ya existiera). Sin scoping por `spaceId`/`opId` en ninguna key
// -- la sesión de WhatsApp es por usuario autenticado, no por space (confirmado:
// `backend/src/whatsapp/infrastructure/whatsAppRouter.js:28`, `backend/index.js:362`).
export const whatsAppStatusKey = ['client', 'whatsapp', 'status'];
export const whatsAppChatsKey = ['client', 'whatsapp', 'chats'];
export const whatsAppMessagesKey = (jid) => ['client', 'whatsapp', 'messages', jid];
// Prefijo compartido por las 3 keys de arriba -- usado por el handler de reconexión (`'connect'`
// en `useWhatsAppSocketSync`) para invalidar todo lo respaldado por REST de una sola vez, sin
// tener que enumerar cada key ni saber qué `jid` está abierto ahora mismo. React Query matchea
// por prefijo de array, así que esto sí alcanza `whatsAppMessagesKey(cualquier jid)`.
const whatsAppKeyPrefix = ['client', 'whatsapp'];
// `whatsapp-presence` no tiene endpoint REST detrás (`GET .../presence` no existe) -- vive solo
// en la cache de React Query como un mapa plano `{[jid]: presences}`, nunca se refetchea (02.7-
// settings.md §0.1/§4: la alternativa de estado local en el componente viola la regla de
// `policies/code.md` de que los eventos de socket que actualizan server-state viven en el hook).
export const whatsAppPresenceKey = ['client', 'whatsapp', 'presence'];

export function useWhatsAppStatus() {
    return useQuery({
        queryKey: whatsAppStatusKey,
        queryFn: () => clientApi.fetchWhatsAppStatus(),
        // Compite con el push del socket (`whatsapp-status`) -- mismo criterio que map/tasks
        // para cualquier query que un socket mantiene viva.
        refetchOnWindowFocus: false,
    });
}

export function useWhatsAppChats({ enabled = true } = {}) {
    return useQuery({
        queryKey: whatsAppChatsKey,
        queryFn: () => clientApi.fetchWhatsAppChats(),
        enabled,
        refetchOnWindowFocus: false,
    });
}

export function useWhatsAppMessages(jid) {
    const qc = useQueryClient();
    return useQuery({
        queryKey: whatsAppMessagesKey(jid),
        queryFn: async () => {
            const d = await clientApi.fetchWhatsAppMessages(jid);
            // El backend ya marca el chat como leído server-side al listar mensajes
            // (`ListMessages.execute` -> `markChatRead`) -- invalida la lista de chats para que
            // `unread_count` se refleje de inmediato, mismo efecto instantáneo que el
            // `setChats(prev => ...)` local que este hook reemplaza.
            qc.invalidateQueries({ queryKey: whatsAppChatsKey });
            return d;
        },
        enabled: !!jid,
        refetchOnWindowFocus: false,
    });
}

export function useWhatsAppTask(taskId) {
    return useQuery({
        queryKey: ['whatsapp_task', taskId],
        queryFn: async () => {
            const res = await clientApi.fetchTask(taskId);
            if (res.success && res.task) return res.task;
            throw new Error("Task not found");
        },
        enabled: !!taskId,
        staleTime: 1000 * 60 * 5,
    });
}

export function useConnectWhatsApp() {
    const qc = useQueryClient();
    const toast = useToast();
    return useMutation({
        // `Connect.execute` es puramente disparado por click (único call site hoy, el botón
        // "Generar Código QR") -- no le agregues auto-retry/auto-reconnect en mount/refocus: hay
        // un test de backend históricamente flaky sobre reconectar cuando ya está conectado
        // (`backend/tests/whatsapp.test.js:68-73`, commit `0e8a4111`, 02.3-client.md §7 riesgo 6c).
        mutationFn: () => clientApi.connectWhatsApp(),
        onSuccess: (d) => qc.setQueryData(whatsAppStatusKey, d),
        onError: (e) => toast.error(getErrorMessage(e)),
    });
}

export function useLogoutWhatsApp() {
    const qc = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: () => clientApi.logoutWhatsApp(),
        onSuccess: (d) => {
            qc.setQueryData(whatsAppStatusKey, d);
            qc.setQueryData(whatsAppChatsKey, { success: true, chats: [] });
        },
        onError: (e) => toast.error(getErrorMessage(e)),
    });
}

// Sin `useMutation` optimista manual (el `setMessages(prev => [...prev, tempMsg])` +
// matching-por-texto que reemplaza) -- el componente arma la burbuja "enviando..." a partir de
// `mutation.isPending`/`mutation.variables` sin tocar la cache compartida (ver WhatsAppPanel.jsx).
// Al resolver, invalida mensajes+chats -- la lista real ya trae el mensaje persistido, sin
// necesidad de reconciliar manualmente contra la respuesta REST (que además nunca trae el DTO
// completo, solo `{success, msgId}`, `SendMessage.execute`) ni contra el eco del socket.
export function useSendWhatsAppMessage(jid) {
    const qc = useQueryClient();
    const toast = useToast();
    return useMutation({
        // integración main→dev, epic B2: `quotedMsgId`/`quotedMsgText`/`quotedSenderJid` solo
        // van en el payload cuando el mensaje es una respuesta a una cita (envío normal = `{ text }`).
        mutationFn: ({ text, quotedMsgId, quotedMsgText, quotedSenderJid }) => {
            const payload = { text };
            if (quotedMsgId) {
                payload.quotedMsgId = quotedMsgId;
                payload.quotedMsgText = quotedMsgText ?? null;
                payload.quotedSenderJid = quotedSenderJid ?? null;
            }
            return clientApi.sendWhatsAppMessage(jid, payload);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: whatsAppMessagesKey(jid) });
            qc.invalidateQueries({ queryKey: whatsAppChatsKey });
        },
        onError: (e) => toast.error(getErrorMessage(e)),
    });
}

// Integración main→dev (catch-up F) -- vincula el chat `childJid` a `parentJid` (fusión en la
// bandeja). Invalida la lista de chats para que el `mergeLinkedChats` del container la rearme.
export function useLinkWhatsAppChat() {
    const qc = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: ({ childJid, parentJid }) => clientApi.linkWhatsAppChat(childJid, parentJid),
        onSuccess: (d) => {
            qc.invalidateQueries({ queryKey: whatsAppChatsKey });
            if (d?.success) toast.success(d.message || 'Chats vinculados correctamente.');
            else toast.error(d?.error || 'Error al vincular chats.');
        },
        onError: (e) => toast.error(getErrorMessage(e)),
    });
}

// integración main→dev, epic B2 -- reenvía un mensaje a `targetJid`. Invalida los mensajes del
// chat destino + la lista de chats (mismo criterio invalidation-first que enviar).
export function useForwardWhatsAppMessage() {
    const qc = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: ({ targetJid, msgId }) => clientApi.forwardWhatsAppMessage(targetJid, msgId),
        onSuccess: (_d, { targetJid }) => {
            qc.invalidateQueries({ queryKey: whatsAppMessagesKey(targetJid) });
            qc.invalidateQueries({ queryKey: whatsAppChatsKey });
        },
        onError: (e) => toast.error(getErrorMessage(e)),
    });
}

// Mismo patrón que arriba -- el envío de media tampoco reconcilia manualmente, invalida y deja
// que la lista real (ya DTO-mapeada) traiga el mensaje persistido.
export function useSendWhatsAppMedia(jid) {
    const qc = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: ({ formData, onUploadProgress }) => clientApi.sendWhatsAppMedia(jid, formData, onUploadProgress),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: whatsAppMessagesKey(jid) });
            qc.invalidateQueries({ queryKey: whatsAppChatsKey });
        },
        onError: (e) => toast.error(getErrorMessage(e)),
    });
}

export function useUploadWhatsAppShortlink() {
    const toast = useToast();
    return useMutation({
        mutationFn: ({ formData, onUploadProgress }) => clientApi.uploadWhatsAppShortlink(formData, onUploadProgress),
        onError: (e) => toast.error(getErrorMessage(e)),
    });
}

// `GET /api/whatsapp/stats` -- 02.7-settings.md §7 riesgo 7: shape verificado contra
// `PgWhatsAppRepository.getStats` (backend), no contra el estado mock de `WhatsAppDashboard.jsx`.
// Devuelve el objeto de stats directo, sin envoltura `{success,...}` (a diferencia de casi todo
// lo demás en este archivo) -- confirmado leyendo `whatsAppRouter.js:49-57`.
export function useWhatsAppStats({ enabled = true } = {}) {
    return useQuery({
        queryKey: [...whatsAppKeyPrefix, 'stats'],
        queryFn: () => clientApi.fetchWhatsAppStats(),
        enabled,
        refetchOnWindowFocus: false,
    });
}

// `PUT /api/whatsapp/settings` -- también sin envoltura `{success,...}`, devuelve
// `{message: 'Settings updated successfully'}` (`whatsAppRouter.js:59-68`). El nombre editado no
// vive en su propia query -- se refleja invalidando `whatsAppStatusKey` (`GetStatus.execute`
// también expone `custom_name`, ver `WhatsAppStatusResponseSchema`).
export function useUpdateWhatsAppSettings() {
    const qc = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: (payload) => clientApi.updateWhatsAppSettings(payload),
        onSuccess: () => qc.invalidateQueries({ queryKey: whatsAppStatusKey }),
        onError: (e) => toast.error(getErrorMessage(e)),
    });
}

export function useUpdateWhatsAppChatPriority() {
    const qc = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: ({ jid, priority }) => clientApi.updateWhatsAppChatPriority(jid, priority),
        onMutate: async ({ jid, priority }) => {
            await qc.cancelQueries({ queryKey: whatsAppChatsKey });
            const previousChats = qc.getQueryData(whatsAppChatsKey);
            if (previousChats?.chats) {
                qc.setQueryData(whatsAppChatsKey, {
                    ...previousChats,
                    chats: previousChats.chats.map(c => 
                        c.chat_jid === jid ? { ...c, priority } : c
                    )
                });
            }
            return { previousChats };
        },
        onError: (err, variables, context) => {
            if (context?.previousChats) {
                qc.setQueryData(whatsAppChatsKey, context.previousChats);
            }
            toast.error(getErrorMessage(err));
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: whatsAppChatsKey });
        },
    });
}

// Precondición real, no cosmética (02.7-settings.md §0.1/§4, confirmado contra
// `BaileysWhatsAppService.js:markChatReadAndSubscribePresence`): sin esta llamada al abrir un
// chat, `whatsapp-presence` nunca se dispara para ese jid en el backend. No escribe nada en
// cache -- el propio evento de presencia (abajo) es el que actualiza `usePresenceMap()`.
export function useMarkWhatsAppChatViewed() {
    const toast = useToast();
    return useMutation({
        mutationFn: (jid) => clientApi.markWhatsAppChatViewed(jid),
        onError: (e) => toast.error(getErrorMessage(e)),
    });
}

export function useWhatsAppChatMedia(jid) {
    return useQuery({
        queryKey: ['whatsapp-chat-media', jid],
        queryFn: () => clientApi.fetchWhatsAppChatMedia(jid),
        enabled: !!jid,
        staleTime: 1000 * 60 * 2,
    });
}

export function useWhatsAppPinMessage() {
    const toast = useToast();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ jid, msgId, isPinned, isFromMe }) => clientApi.pinWhatsAppMessage(jid, msgId, isPinned, isFromMe),
        onSuccess: (data, vars) => {
            qc.setQueryData(whatsAppMessagesKey(vars.jid), (prev) => {
                if (!prev?.messages) return prev;
                return {
                    ...prev,
                    messages: prev.messages.map((m) =>
                        m.msg_id === vars.msgId ? { ...m, is_pinned: vars.isPinned } : m),
                };
            });
        },
        onError: (e) => toast.error(getErrorMessage(e)),
    });
}

export function useWhatsAppReactMessage() {
    const toast = useToast();
    return useMutation({
        mutationFn: ({ jid, msgId, emoji, isFromMe }) => clientApi.reactToWhatsAppMessage(jid, msgId, emoji, isFromMe),
        onError: (e) => toast.error(getErrorMessage(e)),
    });
}

export function useWhatsAppCommonGroups(jid) {
    return useQuery({
        queryKey: ['whatsapp-common-groups', jid],
        queryFn: () => clientApi.getWhatsAppCommonGroups(jid),
        enabled: !!jid,
    });
}

export function useWhatsAppGroupMembers(jid) {
    return useQuery({
        queryKey: ['whatsapp-group-members', jid],
        queryFn: () => clientApi.getWhatsAppGroupMembers(jid),
        enabled: !!jid,
    });
}

export function useCreateWhatsAppGroup() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => clientApi.createWhatsAppGroup(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: whatsAppChatsKey });
        },
    });
}

export function useUpdateWhatsAppGroupParticipants() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ jid, participants, action }) => clientApi.updateWhatsAppGroupParticipants(jid, participants, action),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['whatsapp-group-members', variables.jid] });
        },
    });
}

// Fase 5.16 -- `WhatsPanel.jsx` (presentacional) hacía `qc.setQueryData(whatsAppChatsKey, ...)`
// directo al abrir un chat para poner `unread_count: 0` optimista. Encapsulado acá para que el
// componente no importe `@tanstack/react-query`.
export function useZeroWhatsAppChatUnread() {
    const qc = useQueryClient();
    return useCallback((jid) => {
        qc.setQueryData(whatsAppChatsKey, (prev) => (prev?.chats ? {
            ...prev,
            chats: prev.chats.map((c) => (c.chat_jid === jid ? { ...c, unread_count: 0 } : c)),
        } : prev));
    }, [qc]);
}

// Mapa reactivo `{[jid]: presences}` -- sin `queryFn` real (no hay endpoint REST detrás, ver la
// nota de `whatsAppPresenceKey` arriba). Solo lee lo que el handler de socket de abajo escribe
// vía `setQueryData`; React Query notifica a este `useQuery` igual que si fuera una query real
// porque ambos comparten la misma entrada de cache.
export function usePresenceMap() {
    return useQuery({
        queryKey: whatsAppPresenceKey,
        queryFn: () => ({}),
        initialData: {},
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    }).data;
}

// Suscripción de sockets del feature -- este hook la posee (ver nota de arriba), el componente
// solo la monta una vez.
export function useWhatsAppSocketSync() {
    const qc = useQueryClient();
    useSocket(useMemo(() => ({
        // Excepción deliberada a invalidation-first (02.3-client.md §3/§7 riesgo 6b): estado de
        // conexión efímero (QR/teléfono/status), sin lista respaldada por REST detrás, sin fila
        // persistida -- encaja en la excepción documentada de `policies/code.md` para patches
        // "reaction toggle"-shaped. Es el único de los 3 eventos de WhatsApp que sí la cumple.
        'whatsapp-status': (raw) => {
            const data = WhatsAppStatusSocketSchema.parse(raw);
            qc.setQueryData(whatsAppStatusKey, { success: true, status: data.status, phone: data.phone ?? null, qr: data.qr ?? null });
        },
        // Invalidation-first, NO `setQueryData` con el payload del socket -- hallazgo real
        // encontrado al implementar este hook: `BaileysWhatsAppService.js` emite las filas
        // CRUDAS del repositorio (`await whatsAppRepository.listChats(sessionName)`, sin pasar
        // por `toWhatsAppChatDTO`), mientras que `GET /api/whatsapp/chats` (`ListChats.js`) sí
        // las mapea al DTO antes de responder -- son shapes distintos del mismo evento lógico.
        // Usar el payload del socket directo aquí metería filas sin DTO-mapear a una cache que
        // `WhatsAppChatsResponseSchema` valida contra el shape DTO-mapeado. Invalidar y dejar
        // que la REST real (ya DTO-mapeada) sea la única fuente de verdad evita ese desajuste de
        // shape, además de ser lo que exige `mejoras-futuras.md` ítem 7 para CRUD de fila (una
        // lista de chats es dominio persistido, no un "reaction toggle").
        'whatsapp-chats': () => {
            qc.invalidateQueries({ queryKey: whatsAppChatsKey });
        },
        // Mismo razonamiento que arriba -- un mensaje es una fila persistida
        // (`clientApi.fetchWhatsAppMessages` es un endpoint REST real). Solo se valida
        // `chat_jid`: con invalidation-first, el contenido de `message` ya no se consume
        // directamente (ver `useSendWhatsAppMessage` arriba) -- una invalidación vuelve a pedir
        // la lista real del servidor, sin necesitar el matching manual
        // msg_id/from_me/status/text_content que tenía el código anterior (una fuente real de
        // bugs: 2 caminos de reconciliación independientes -- el `onSuccess` del REST de enviar
        // y el eco del socket -- podían pisarse entre sí).
        'new-whats-message': (raw) => {
            const data = NewWhatsMessageEnvelopeSchema.parse(raw);
            qc.invalidateQueries({ queryKey: whatsAppMessagesKey(data.chat_jid) });
            qc.invalidateQueries({ queryKey: whatsAppChatsKey });
            
            // Notification for incoming messages
            if (data.message && data.message.from_me === false) {
                // If it's a group, chat_jid is the group, sender_jid is the person
                const phoneOrName = data.message.push_name || 
                                   data.message.sender_jid?.split('@')[0] || 
                                   data.chat_jid?.split('@')[0] || 
                                   'WhatsApp';
                                   
                let preview = data.message.text_content || '';
                if (!preview && data.message.media_type) {
                    preview = '📷 Media';
                }
                
                if (preview.length > 40) preview = preview.substring(0, 40) + '...';
                
                toastSink.info(`${phoneOrName}: ${preview}`);
            }
        },
        // Excepción "reaction toggle" análoga a `whatsapp-status` -- un patch puntual de un campo
        // (`status`) de un mensaje ya cacheado, no un cambio de forma de lista. Invalidar aquí
        // sería desperdiciar un roundtrip completo por cada tick de entrega/lectura (pueden
        // llegar varios por segundo en un chat activo).
        'whatsapp-message-update': (raw) => {
            const data = WhatsAppMessageUpdateSocketSchema.parse(raw);
            qc.setQueryData(whatsAppMessagesKey(data.chat_jid), (prev) => {
                if (!prev?.messages) return prev;
                return {
                    ...prev,
                    messages: prev.messages.map((m) =>
                        m.msg_id === data.msg_id ? { ...m, status: data.status } : m),
                };
            });
        },
        'whatsapp-message-pin': (data) => {
            qc.setQueryData(whatsAppMessagesKey(data.chat_jid), (prev) => {
                if (!prev?.messages) return prev;
                return {
                    ...prev,
                    messages: prev.messages.map((m) =>
                        m.msg_id === data.msg_id ? { ...m, is_pinned: data.is_pinned } : m),
                };
            });
        },
        'whatsapp-message-reaction': (raw) => {
            const data = raw; // Could use Zod schema here
            qc.setQueryData(whatsAppMessagesKey(data.chat_jid), (prev) => {
                if (!prev?.messages) return prev;
                return {
                    ...prev,
                    messages: prev.messages.map((m) => m.msg_id === data.msg_id ? { ...m, reactions: data.reactions } : m),
                };
            });
        },
        // Sin endpoint REST detrás (ver `whatsAppPresenceKey` arriba) -- el único lugar donde
        // este evento se refleja es la propia cache de presencia. Precondición real para que
        // esto llegue a dispararse: `useMarkWhatsAppChatViewed()` debe haberse llamado para ese
        // jid (02.7-settings.md §0.1/§4).
        'whatsapp-presence': (raw) => {
            const data = WhatsAppPresenceSocketSchema.parse(raw);
            qc.setQueryData(whatsAppPresenceKey, (prev) => ({ ...(prev || {}), [data.jid]: data.presences }));
        },
        // Recuperación tras reconexión (02.7-settings.md §7 riesgo 11): el server de Socket.IO de
        // este proyecto no tiene `connectionStateRecovery` configurado (confirmado leyendo
        // `backend/index.js:160-167`), así que un evento emitido mientras el cliente estaba
        // desconectado se pierde para siempre, no se reproduce al reconectar. Antes, los 3
        // `setInterval` de `WhatsPanel.jsx`/`WhatsAppDashboard.jsx` disimulaban esto por su cuenta
        // (se autocorregían en el siguiente tick de polling); una vez que este hook es 100%
        // socket-driven (sin `refetchInterval` en ninguna query), hay que re-invalidar todo lo
        // respaldado por REST de este feature explícitamente en cada reconexión.
        'connect': () => {
            qc.invalidateQueries({ queryKey: whatsAppKeyPrefix });
        },
    }), [qc]));
}
